import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const DemoModal = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    whatsapp: '',
    email: '',
    interest: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSuccess(true);
    toast.success('Solicitação enviada com sucesso!');

    // Reset after 2 seconds
    setTimeout(() => {
      setIsSuccess(false);
      setFormData({
        name: '',
        company: '',
        role: '',
        whatsapp: '',
        email: '',
        interest: '',
      });
      onClose();
    }, 2000);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="demo-modal">
        {isSuccess ? (
          <div className="py-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Solicitação Enviada!
            </h3>
            <p className="text-slate-600">
              Em breve um especialista entrará em contato.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">
                Solicitar Demonstração
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Preencha o formulário e um especialista entrará em contato para agendar sua demonstração gratuita.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome completo</Label>
                  <Input
                    id="name"
                    data-testid="demo-input-name"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Empresa</Label>
                  <Input
                    id="company"
                    data-testid="demo-input-company"
                    placeholder="Nome da empresa"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Cargo</Label>
                  <Input
                    id="role"
                    data-testid="demo-input-role"
                    placeholder="Seu cargo"
                    value={formData.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp</Label>
                  <Input
                    id="whatsapp"
                    data-testid="demo-input-whatsapp"
                    placeholder="(00) 00000-0000"
                    value={formData.whatsapp}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-mail corporativo</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="demo-input-email"
                  placeholder="seu@empresa.com.br"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="interest">Principal interesse</Label>
                <Select
                  value={formData.interest}
                  onValueChange={(value) => handleChange('interest', value)}
                >
                  <SelectTrigger data-testid="demo-select-interest">
                    <SelectValue placeholder="Selecione uma opção" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plataforma-ead">Plataforma EAD / Faculdade Corporativa</SelectItem>
                    <SelectItem value="producao-cursos">Produção de Cursos</SelectItem>
                    <SelectItem value="nr01">NR-01 Psicossocial</SelectItem>
                    <SelectItem value="pacote-completo">Pacote Completo</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                data-testid="demo-submit-btn"
                disabled={isSubmitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-semibold mt-6"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Solicitar Demonstração Gratuita'
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
